import { useState } from "react";
import { ChevronDown, ChevronRight, Gavel, MessageSquare, Eye, AlertTriangle } from "lucide-react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { CouncilPanel } from "@/features/council";
import { formatRelativeTime } from "@/lib/format";
import { useRecentDebates } from "@/sdk/hooks/use-alpha";
import type {
  Debate,
  DebateBlindVote,
  DebateCrossExamVote,
} from "@/sdk/hooks/use-alpha";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AGENT_COLORS: Record<string, string> = {
  quant: "bg-blue-500",
  skeptic: "bg-amber-500",
  scout: "bg-emerald-500",
  judge: "bg-purple-500",
};

const AGENT_TEXT_COLORS: Record<string, string> = {
  quant: "text-blue-400",
  skeptic: "text-amber-400",
  scout: "text-emerald-400",
  judge: "text-purple-400",
};

const AGENT_LABELS: Record<string, string> = {
  quant: "Quant",
  skeptic: "Skeptic",
  scout: "Scout",
  judge: "Judge",
};

// ---------------------------------------------------------------------------
// Small Components
// ---------------------------------------------------------------------------

function AgentBadge({ agentId }: { agentId: string }) {
  const color = AGENT_TEXT_COLORS[agentId] ?? "text-white/60";
  const label = AGENT_LABELS[agentId] ?? agentId;
  return (
    <span className={`text-xs font-semibold uppercase tracking-wide ${color}`}>
      {label}
    </span>
  );
}

function ProbabilityBar({
  value,
  agentId,
  label,
}: {
  value: number | null;
  agentId: string;
  label?: string;
}) {
  const pct = value != null ? Math.round(value * 100) : 0;
  const barColor = AGENT_COLORS[agentId] ?? "bg-white/30";

  return (
    <div className="flex items-center gap-2">
      <AgentBadge agentId={agentId} />
      <div className="flex-1 h-5 rounded bg-white/5 relative overflow-hidden">
        <div
          className={`h-full rounded ${barColor} opacity-70 transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
        <span className="absolute inset-0 flex items-center justify-center text-[0.65rem] font-mono font-bold text-white/90">
          {value != null ? `${(value * 100).toFixed(1)}%` : "n/a"}
        </span>
      </div>
      {label && (
        <span className="text-[0.6rem] text-muted w-12 text-right">
          {label}
        </span>
      )}
    </div>
  );
}

function DeltaArrow({ delta }: { delta: number | null }) {
  if (delta == null) return <span className="text-white/20">--</span>;
  const absDelta = Math.abs(delta * 100);
  if (absDelta < 0.1) return <span className="text-white/30">~0</span>;
  const isPositive = delta > 0;
  return (
    <span
      className={`text-xs font-mono font-bold ${isPositive ? "text-green-400" : "text-red-400"}`}
    >
      {isPositive ? "+" : ""}
      {(delta * 100).toFixed(1)}pp
    </span>
  );
}

function DirectionBadge({ direction }: { direction: string | null }) {
  if (!direction) return null;
  const isYes = direction.toUpperCase() === "YES";
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${
        isYes
          ? "bg-emerald-500/20 text-emerald-300"
          : "bg-red-500/20 text-red-300"
      }`}
    >
      {direction.toUpperCase()}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Phase 1: Blind Vote Section
// ---------------------------------------------------------------------------

function BlindVotePhase({ votes }: { votes: DebateBlindVote[] }) {
  if (votes.length === 0) {
    return (
      <p className="text-xs text-white/30 italic">No blind vote data recorded.</p>
    );
  }

  // Find outlier: agent whose probability is furthest from the mean
  const probs = votes
    .map((v) => v.probability)
    .filter((p): p is number => p != null);
  const mean = probs.length > 0 ? probs.reduce((a, b) => a + b, 0) / probs.length : 0;
  let outlierAgent = "";
  let maxDeviation = 0;
  for (const v of votes) {
    if (v.probability != null) {
      const dev = Math.abs(v.probability - mean);
      if (dev > maxDeviation) {
        maxDeviation = dev;
        outlierAgent = v.agent_id;
      }
    }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 mb-2">
        <Eye className="h-3.5 w-3.5 text-blue-400" />
        <span className="text-xs font-semibold text-blue-400 uppercase tracking-wide">
          Phase 1: Blind Vote
        </span>
      </div>
      {votes.map((vote) => (
        <div
          key={vote.agent_id}
          className={`${
            vote.agent_id === outlierAgent && maxDeviation > 0.1
              ? "ring-1 ring-amber-500/40 rounded p-1"
              : ""
          }`}
        >
          <ProbabilityBar
            value={vote.probability}
            agentId={vote.agent_id}
            label={
              vote.latency_ms != null
                ? `${(vote.latency_ms / 1000).toFixed(1)}s`
                : undefined
            }
          />
        </div>
      ))}
      {outlierAgent && maxDeviation > 0.1 && (
        <p className="text-[0.6rem] text-amber-400/70 mt-1">
          * {AGENT_LABELS[outlierAgent] ?? outlierAgent} is the outlier
          (deviation: {(maxDeviation * 100).toFixed(1)}pp from mean)
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 2: Cross-Examination Section
// ---------------------------------------------------------------------------

function CrossExamPhase({ votes }: { votes: DebateCrossExamVote[] }) {
  if (votes.length === 0) {
    return (
      <p className="text-xs text-white/30 italic">
        No cross-examination (spread below threshold).
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="h-3.5 w-3.5 text-amber-400" />
        <span className="text-xs font-semibold text-amber-400 uppercase tracking-wide">
          Phase 2: Cross-Examination
        </span>
      </div>
      <div className="space-y-2">
        {votes.map((vote) => {
          const hasDelta =
            vote.probability_delta != null && Math.abs(vote.probability_delta) > 0.001;
          return (
            <div
              key={vote.agent_id}
              className={`rounded border px-3 py-2 ${
                hasDelta
                  ? "border-amber-500/25 bg-amber-500/5"
                  : "border-white/5 bg-white/[0.02]"
              }`}
            >
              <div className="flex items-center justify-between">
                <AgentBadge agentId={vote.agent_id} />
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-white/50">
                    {vote.probability_before != null
                      ? `${(vote.probability_before * 100).toFixed(1)}%`
                      : "?"}
                  </span>
                  <span className="text-white/30">&rarr;</span>
                  <span className="text-xs font-mono text-white/80 font-bold">
                    {vote.probability_after != null
                      ? `${(vote.probability_after * 100).toFixed(1)}%`
                      : "?"}
                  </span>
                  <DeltaArrow delta={vote.probability_delta} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 3: Judge Verdict Section
// ---------------------------------------------------------------------------

function JudgeVerdictPhase({
  judge,
}: {
  judge: Debate["judge"];
}) {
  if (judge.final_probability == null && !judge.direction) {
    return (
      <p className="text-xs text-white/30 italic">No judge verdict recorded.</p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-2">
        <Gavel className="h-3.5 w-3.5 text-purple-400" />
        <span className="text-xs font-semibold text-purple-400 uppercase tracking-wide">
          Phase 3: Judge Verdict
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded border border-white/10 bg-white/[0.03] px-2.5 py-2 text-center">
          <div className="text-[0.65rem] text-muted">Probability</div>
          <div className="text-sm font-mono font-bold text-white/90">
            {judge.final_probability != null
              ? `${(judge.final_probability * 100).toFixed(1)}%`
              : "n/a"}
          </div>
        </div>
        <div className="rounded border border-white/10 bg-white/[0.03] px-2.5 py-2 text-center">
          <div className="text-[0.65rem] text-muted">Confidence</div>
          <div className="text-sm font-mono font-bold text-white/90">
            {judge.confidence != null
              ? `${(judge.confidence * 100).toFixed(0)}%`
              : "n/a"}
          </div>
        </div>
        <div className="rounded border border-white/10 bg-white/[0.03] px-2.5 py-2 text-center">
          <div className="text-[0.65rem] text-muted">Edge</div>
          <div
            className={`text-sm font-mono font-bold ${
              judge.edge != null && judge.edge > 0
                ? "text-green-400"
                : "text-white/60"
            }`}
          >
            {judge.edge != null ? `${(judge.edge * 100).toFixed(1)}%` : "n/a"}
          </div>
        </div>
        <div className="rounded border border-white/10 bg-white/[0.03] px-2.5 py-2 text-center">
          <div className="text-[0.65rem] text-muted">Kelly</div>
          <div className="text-sm font-mono font-bold text-white/90">
            {judge.kelly_fraction != null
              ? `${(judge.kelly_fraction * 100).toFixed(1)}%`
              : "n/a"}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs text-muted">Direction:</span>
        <DirectionBadge direction={judge.direction} />
        {judge.recommended_action && (
          <>
            <span className="text-white/20">|</span>
            <span className="text-xs text-white/50">
              {judge.recommended_action}
            </span>
          </>
        )}
      </div>

      {judge.key_risks.length > 0 && (
        <div className="mt-2 space-y-1">
          <div className="flex items-center gap-1 text-xs text-red-400/70">
            <AlertTriangle className="h-3 w-3" />
            <span>Key Risks</span>
          </div>
          <ul className="list-disc list-inside text-[0.65rem] text-white/50 space-y-0.5 pl-1">
            {judge.key_risks.map((risk, i) => (
              <li key={i}>{risk}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expandable Debate Card
// ---------------------------------------------------------------------------

function DebateCard({ debate }: { debate: Debate }) {
  const [expanded, setExpanded] = useState(false);
  const [showRationale, setShowRationale] = useState(false);

  const hasAgreement =
    debate.blind_votes.length > 1 &&
    debate.max_cross_exam_shift != null &&
    debate.max_cross_exam_shift < 0.05;

  const borderColor = debate.cross_exam_changed_outcome
    ? "border-amber-500/30"
    : hasAgreement
      ? "border-green-500/20"
      : "border-white/10";

  return (
    <div
      className={`rounded-lg border ${borderColor} bg-white/[0.02] overflow-hidden`}
    >
      {/* Header (always visible) */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 text-left hover:bg-white/[0.02] transition"
      >
        <div className="mt-0.5 shrink-0">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white/85 leading-snug">
            {debate.question || "Unknown market"}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span className="text-[0.65rem] text-white/35">
              {formatRelativeTime(debate.created_at)}
            </span>
            {debate.consensus_probability != null && (
              <span className="text-[0.65rem] font-mono text-white/50">
                P={`${(debate.consensus_probability * 100).toFixed(1)}%`}
              </span>
            )}
            <DirectionBadge direction={debate.judge.direction} />
            {debate.cross_exam_changed_outcome && (
              <span className="text-[0.6rem] bg-amber-500/20 text-amber-300 rounded px-1 py-0.5">
                cross-exam shifted
              </span>
            )}
            {debate.models_queried.length > 0 && (
              <span className="text-[0.6rem] text-white/25">
                {debate.models_queried.length} models
              </span>
            )}
          </div>
        </div>
        {/* Edge badge on the right */}
        {debate.judge.edge != null && (
          <div className="shrink-0">
            <span
              className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${
                debate.judge.edge > 0.1
                  ? "bg-green-500/20 text-green-300"
                  : debate.judge.edge > 0.05
                    ? "bg-amber-500/20 text-amber-300"
                    : "bg-white/10 text-white/50"
              }`}
            >
              {(debate.judge.edge * 100).toFixed(1)}% edge
            </span>
          </div>
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 md:px-4 pb-3 md:pb-4 space-y-3 md:space-y-4 border-t border-white/5 pt-3">
          <BlindVotePhase votes={debate.blind_votes} />
          <CrossExamPhase votes={debate.cross_exam_votes} />
          <JudgeVerdictPhase judge={debate.judge} />

          {/* Rationale toggle */}
          {debate.judge.rationale && (
            <div>
              <button
                type="button"
                onClick={() => setShowRationale(!showRationale)}
                className="text-[0.7rem] text-muted hover:text-white/60 transition underline"
              >
                {showRationale ? "Hide rationale" : "Show full rationale"}
              </button>
              {showRationale && (
                <p className="mt-1.5 text-[0.7rem] text-white/50 leading-relaxed whitespace-pre-wrap bg-white/[0.03] rounded p-2">
                  {debate.judge.rationale}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function DebateCouncilPage() {
  const { data, isLoading, dataUpdatedAt } = useRecentDebates();

  const debates = data?.debates ?? [];
  const stats = data?.stats;

  const isLive = dataUpdatedAt > 0 && Date.now() - dataUpdatedAt < 120_000;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Legacy multi-provider council (/api/council/*) — deliberately titled
          to distinguish it from the alpha Tier-3 debate council below. */}
      <CouncilPanel />

      {/* Summary stats */}
      <Panel
        title="Debate Council"
        description="3-phase adversarial debate visualization"
        actions={
          <div className="flex items-center gap-2 text-xs text-muted">
            <span
              className={`h-2 w-2 rounded-full ${
                isLive ? "bg-green-500 animate-pulse" : "bg-muted/50"
              }`}
            />
            {isLive ? "Live" : "Stale"}
          </div>
        }
      >
        <div className="grid gap-2 md:gap-3 grid-cols-1 sm:grid-cols-3">
          <StatCard
            label="Total Debates"
            value={String(stats?.total_debates ?? 0)}
            hint="Debates with audit trail"
          />
          <StatCard
            label="Avg Cross-Exam Shift"
            value={
              stats?.avg_cross_exam_shift != null
                ? `${(stats.avg_cross_exam_shift * 100).toFixed(1)}pp`
                : "n/a"
            }
            tone={
              stats?.avg_cross_exam_shift != null && stats.avg_cross_exam_shift > 0.05
                ? "warning"
                : "default"
            }
            hint="Mean probability change"
          />
          <StatCard
            label="Cross-Exam Changed"
            value={String(stats?.debates_with_cross_exam_change ?? 0)}
            tone={
              (stats?.debates_with_cross_exam_change ?? 0) > 0 ? "warning" : "default"
            }
            hint="Debates where cross-exam shifted outcome"
          />
        </div>
      </Panel>

      {/* Debate list */}
      <Panel title="Recent Debates">
        {isLoading ? (
          <LoadingState text="Loading debates..." />
        ) : debates.length === 0 ? (
          <div className="py-8 text-center">
            <Gavel className="mx-auto h-8 w-8 text-white/20" />
            <p className="mt-2 text-sm text-muted">
              No debate council sessions recorded yet.
            </p>
            <p className="text-xs text-white/25">
              Debates appear after the alpha pipeline runs Tier 3 analysis.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {debates.map((debate) => (
              <DebateCard key={debate.debate_id} debate={debate} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
